import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { goToRecruitLook, RL_COACH_DASHBOARD_PATH, recruitlookUrl } from '@/lib/recruitlookHandoff';

interface Props {
  /** Full external URL to redirect to. Takes precedence over `next`. */
  href?: string;
  /** Path on RecruitLook to land on. */
  next?: string;
}

/**
 * Coach dashboards live on RecruitLook. Any old RLevents coach URL (bookmark,
 * old email) lands here and is handed over — signed in where possible — rather
 * than showing a 404.
 *
 * When `href` is supplied, it performs a plain external redirect instead of the
 * signed handoff (useful for public content like /athletes).
 */
const RedirectToRecruitLook: React.FC<Props> = ({ href, next = RL_COACH_DASHBOARD_PATH }) => {
  React.useEffect(() => {
    if (href) {
      window.location.replace(href);
      return;
    }
    goToRecruitLook(next);
  }, [href, next]);

  const manualHref = href ?? recruitlookUrl(next);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center">
      <div className="space-y-4">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
        <h1 className="text-xl font-semibold">Taking you to RecruitLook…</h1>
        <p className="text-sm text-muted-foreground max-w-sm">
          Your teams, rosters and players live on RecruitLook.
        </p>
        <p className="text-sm">
          <a className="underline" href={manualHref}>
            Continue manually
          </a>
        </p>
      </div>
    </div>
  );
};

/**
 * Redirect an old /profile/:username URL to the matching RecruitLook profile.
 */
export const ProfileRedirect: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const href = `https://recruitlook.com/profile/${encodeURIComponent(username ?? '')}`;
  return <RedirectToRecruitLook href={href} />;
};

export default RedirectToRecruitLook;
